import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useHotel } from '../context/HotelContext';
import { useLanguage } from '../context/LanguageContext';
import OptimizedImage from './OptimizedImage';
import { PhotoNote } from './Trust';

const Gallery = () => {
  const { gallery } = useHotel();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedImage, setSelectedImage] = useState(null);
  const { lang, t } = useLanguage();
  const captionOf = (img) => {
    const c = img.caption_translations || {};
    return c[lang] || c.en || img.alt;
  };

  // ImageObject structured data so Google Images can index captions + location.
  useEffect(() => {
    const withCaption = (gallery || []).filter((g) => (g.caption_translations || {}).en);
    const id = 'gallery-jsonld';
    document.getElementById(id)?.remove();
    if (!withCaption.length) return undefined;
    const origin = (process.env.REACT_APP_SITE_URL || 'https://larosa-negra.com').replace(/\/$/, '');
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ImageGallery',
      name: 'Hotel La Rosa Negra – photo gallery',
      about: { '@type': 'Hotel', name: 'Hotel La Rosa Negra' },
      image: withCaption.slice(0, 60).map((g) => ({
        '@type': 'ImageObject',
        contentUrl: g.url.startsWith('http') ? g.url : origin + g.url,
        caption: captionOf(g),
        name: captionOf(g),
        inLanguage: lang,
        ...(g.location_tag ? { contentLocation: { '@type': 'Place', name: g.location_tag } } : {}),
      })),
    });
    document.head.appendChild(script);
    return () => document.getElementById(id)?.remove();
  }, [gallery, lang]); // eslint-disable-line react-hooks/exhaustive-deps
  const [loadMore, setLoadMore] = useState(false);

  const categories = ['All', ...new Set(gallery.map(img => img.category))];
  
  const filteredImages = selectedCategory === 'All' 
    ? gallery 
    : gallery.filter(img => img.category === selectedCategory);

  const displayedImages = loadMore ? filteredImages : filteredImages.slice(0, 6);

  return (
    <section id="gallery" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <p className="text-orange-500 font-semibold mb-2 text-sm uppercase tracking-wider">{t('gallery.subtitle')}</p>
          <h2 className="text-4xl lg:text-5xl font-bold mb-4 text-gray-900">{t('gallery.title')}</h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">{t('gallery.intro')}</p>
          <PhotoNote className="mt-2" />
        </div>

        {/* Category filters */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => {
                setSelectedCategory(category);
                setLoadMore(false);
              }}
              className={`px-6 py-2.5 rounded-full font-semibold transition-all ${
                selectedCategory === category
                  ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border-2 border-gray-200'
              }`}
            >
              {category} ({category === 'All' ? gallery.length : gallery.filter(img => img.category === category).length})
            </button>
          ))}
        </div>

        {/* Image grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayedImages.map((image, index) => (
            <div 
              key={image.id || image.url || `gallery-${index}`}
              className="relative h-80 rounded-2xl overflow-hidden cursor-pointer group shadow-lg hover:shadow-2xl transition-all duration-300"
              onClick={() => setSelectedImage(image)}
            >
              <OptimizedImage 
                src={image.url} 
                alt={captionOf(image)} 
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="absolute bottom-4 left-4 right-4 space-y-1.5">
                  <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    {image.category}
                  </span>
                  {(image.caption_translations || {}).en && (
                    <p className="text-white text-sm leading-snug line-clamp-2" data-testid="gallery-caption">{captionOf(image)}{image.location_tag ? ` · ${image.location_tag}` : ''}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Load more button */}
        {filteredImages.length > 6 && !loadMore && (
          <div className="text-center mt-12">
            <button 
              onClick={() => setLoadMore(true)}
              className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-10 py-4 rounded-full font-bold text-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl"
            >
              Load More ({filteredImages.length - 6}+)
            </button>
          </div>
        )}

        {loadMore && (
          <div className="text-center mt-8">
            <p className="text-gray-600">Showing all {filteredImages.length} images</p>
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button 
            onClick={() => setSelectedImage(null)}
            className="absolute top-8 right-8 bg-white rounded-full p-3 hover:bg-gray-100 z-10"
          >
            <X size={24} />
          </button>
          <div className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            <OptimizedImage 
              src={selectedImage.url} 
              alt={captionOf(selectedImage)} 
              className="w-full h-auto rounded-lg"
              loading="eager"
            />
            <div className="text-center mt-4 space-y-2">
              <span className="bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                {selectedImage.category}
              </span>
              {(selectedImage.caption_translations || {}).en && (
                <p className="text-gray-200 text-sm md:text-base" data-testid="lightbox-caption">
                  {captionOf(selectedImage)}{selectedImage.location_tag ? <span className="text-gray-400"> · {selectedImage.location_tag}</span> : null}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;